import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, linkedSignal, signal } from '@angular/core';

import { environment } from '../../environments/environment';

export type AnalyticsConsentPreference = 'accepted' | 'denied';

export const ANALYTICS_CONSENT_STORAGE_KEY = 'analytics-consent-v1';

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private document = inject(DOCUMENT);

  readonly isAnalyticsConfigured = Boolean(environment.firebase.measurementId);
  readonly preference = signal<AnalyticsConsentPreference | null>(this.loadPreference());
  readonly isBannerOpen = linkedSignal(() => (this.isAnalyticsConfigured ? this.preference() === null : false));
  readonly hasAccepted = computed(() => this.preference() === 'accepted');

  openBanner() {
    this.isBannerOpen.set(true);
  }

  accept() {
    this.setPreference('accepted');
  }

  deny() {
    this.setPreference('denied');
  }

  private setPreference(preference: AnalyticsConsentPreference) {
    if (!this.isAnalyticsConfigured) {
      return;
    }
    this.preference.set(preference);
    this.isBannerOpen.set(false);
    if (this.canUseStorage()) {
      localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, preference);
    }
  }

  private loadPreference(): AnalyticsConsentPreference | null {
    if (!this.isAnalyticsConfigured || !this.canUseStorage()) {
      return null;
    }
    const value = localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (value === 'accepted' || value === 'denied') {
      return value;
    }
    return null;
  }

  clearAnalyticsCookies() {
    const measurementCookieSuffix = environment.firebase.measurementId?.replace(/^G-/, '');
    const cookieNames = ['_ga', '_gid', '_gat'];
    if (measurementCookieSuffix) {
      cookieNames.push(`_ga_${measurementCookieSuffix}`);
    }
    for (const cookieName of cookieNames) {
      this.expireCookie(cookieName);
      for (const domain of this.getCookieDomains()) {
        this.expireCookie(cookieName, domain);
      }
    }
  }

  private expireCookie(name: string, domain?: string) {
    const domainValue = domain ? `;domain=${domain}` : '';
    this.document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainValue};SameSite=Lax`;
  }

  private getCookieDomains() {
    const hostname = this.document.location?.hostname;
    if (!hostname || !hostname.includes('.')) {
      return [];
    }
    const parts = hostname.split('.');
    return parts.slice(1).map((_, index) => `.${parts.slice(index + 1).join('.')}`);
  }

  private canUseStorage() {
    return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
  }
}
